import { getCurrencyDecimals, getSubunitMultiplier, majorToAmount } from '@bbpos/utils';
import { useEffect, useState } from 'react';
import { Button, Input, Checkbox, Modal, Select, Toggle, useToast } from '@bbpos/ui';
import {
  PLAN_FEATURE_KEYS,
  SUPPORTED_CURRENCIES,
  isPlanFeatureKey,
  type PlanFeatureKey,
  type PlanSupportLevel,
} from '@bbpos/types';
import { subscriptionService } from '@/services/subscription.service';
import type {
  CreatePlanPayload,
  PlanCurrencyPrice,
  SubscriptionPlan,
  UpdatePlanPayload,
} from '@/types/subscription';
import { getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { cn } from './design-tokens';
import {
  METERED_LIMITS,
  groupPlanFeatures,
  limitInputLabel,
  type MeteredLimitColumn,
} from './plan-entitlements';

interface PlanEditorModalProps {
  open: boolean;
  plan?: SubscriptionPlan | null;
  onClose: () => void;
  onSaved: () => void;
}

const FEATURE_GROUPS = groupPlanFeatures();

const SUPPORT_LEVEL_OPTIONS: Array<{ value: '' | PlanSupportLevel; label: string }> = [
  { value: '', label: 'No support level' },
  { value: 'EMAIL', label: 'Email support' },
  { value: 'PRIORITY', label: 'Priority support' },
  { value: 'DEDICATED', label: 'Dedicated support' },
];

/** INR is the base price above; each other tenant currency may have its own (QA SS4-001). */
const PRICE_CURRENCIES = SUPPORTED_CURRENCIES.filter((c) => c !== 'INR');

/** Major units as typed in an input: no grouping, the currency's own decimals. */
function toMajorInput(amount: number, currency: string): string {
  return (amount / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency));
}

const LABEL_CLASS =
  'block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1.5';

interface FormState {
  name: string;
  display_name: string;
  description: string;
  price_monthly_rupees: string;
  price_yearly_rupees: string;
  max_locations: string;
  max_users: string;
  max_requests_per_minute: string;
  /** Blank = unlimited. */
  limits: Record<MeteredLimitColumn, string>;
  /** Per currency, in major units; both blank = no price (its tenants pay the INR price). */
  prices: Record<string, { monthly: string; yearly: string }>;
  sort_order: string;
  features: PlanFeatureKey[];
  show_on_website: boolean;
  badge: string;
  support_level: '' | PlanSupportLevel;
  is_active: boolean;
}

function toFormState(subunit: number, plan: SubscriptionPlan | null | undefined): FormState {
  const limits = {} as Record<MeteredLimitColumn, string>;
  for (const l of METERED_LIMITS) limits[l.column] = plan?.[l.column]?.toString() ?? '';
  const prices: FormState['prices'] = {};
  for (const c of PRICE_CURRENCIES) {
    const own = plan?.prices?.find((p) => p.currency === c);
    prices[c] = own
      ? { monthly: toMajorInput(own.price_monthly, c), yearly: toMajorInput(own.price_yearly, c) }
      : { monthly: '', yearly: '' };
  }

  if (!plan) {
    return {
      name: '',
      display_name: '',
      description: '',
      price_monthly_rupees: '0',
      price_yearly_rupees: '0',
      max_locations: '1',
      max_users: '5',
      max_requests_per_minute: '',
      limits,
      prices,
      sort_order: '10',
      features: [],
      show_on_website: false,
      badge: '',
      support_level: '',
      is_active: true,
    };
  }
  return {
    name: plan.name,
    display_name: plan.display_name,
    description: plan.description ?? '',
    price_monthly_rupees: String(Math.round(plan.price_monthly / subunit)),
    price_yearly_rupees: String(Math.round(plan.price_yearly / subunit)),
    max_locations: String(plan.max_locations),
    max_users: String(plan.max_users),
    max_requests_per_minute: plan.max_requests_per_minute?.toString() ?? '',
    limits,
    prices,
    sort_order: String(plan.sort_order),
    // A retired key (e.g. `basic_pos`) is dropped: the API refuses it on save.
    features: (plan.features ?? []).filter(isPlanFeatureKey),
    show_on_website: plan.show_on_website ?? false,
    badge: plan.badge ?? '',
    support_level: plan.support_level ?? '',
    is_active: plan.is_active,
  };
}

/** Blank means "no limit" and is sent as null, so a saved limit can be cleared. */
function blankToNull(raw: string): number | null {
  return raw.trim() === '' ? null : Number(raw);
}

const isWholeNumber = (raw: string) => /^\d+$/.test(raw.trim());

export function PlanEditorModal({ open, plan, onClose, onSaved }: PlanEditorModalProps) {
  const isEdit = Boolean(plan);
  const { toast } = useToast();
  // Declared before the form state, which seeds from them.
  const currency = 'INR'; // the base price (price_monthly / price_yearly) is INR paise
  const currencySymbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const [form, setForm] = useState<FormState>(() => toFormState(subunit, plan));
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setForm(toFormState(subunit, plan));
      setErrors({});
    }
  }, [open, plan, subunit]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function setLimit(column: MeteredLimitColumn, value: string) {
    setForm((f) => ({ ...f, limits: { ...f.limits, [column]: value } }));
  }

  function setPrice(currency: string, cycle: 'monthly' | 'yearly', value: string) {
    setForm((f) => ({
      ...f,
      prices: { ...f.prices, [currency]: { ...f.prices[currency], [cycle]: value } },
    }));
  }

  function setFeature(feature: PlanFeatureKey, on: boolean) {
    setForm((f) => ({
      ...f,
      features: on ? [...f.features, feature] : f.features.filter((x) => x !== feature),
    }));
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    else if (!/^[A-Z][A-Z0-9_]{1,29}$/.test(form.name.trim())) {
      e.name = 'Use UPPER_SNAKE_CASE, 2–30 chars';
    }
    if (!form.display_name.trim()) e.display_name = 'Display name is required';
    if (Number.isNaN(Number(form.price_monthly_rupees)) || Number(form.price_monthly_rupees) < 0) {
      e.price_monthly_rupees = 'Enter a valid amount';
    }
    if (Number.isNaN(Number(form.price_yearly_rupees)) || Number(form.price_yearly_rupees) < 0) {
      e.price_yearly_rupees = 'Enter a valid amount';
    }
    if (Number(form.max_locations) < 1) e.max_locations = 'Must be ≥ 1';
    if (Number(form.max_users) < 1) e.max_users = 'Must be ≥ 1';
    // Blank is meaningful here: it means the plan sets no API ceiling.
    if (form.max_requests_per_minute.trim() !== '' && Number(form.max_requests_per_minute) < 1) {
      e.max_requests_per_minute = 'Must be ≥ 1, or blank for no limit';
    }
    for (const l of METERED_LIMITS) {
      const raw = form.limits[l.column];
      if (raw.trim() !== '' && !isWholeNumber(raw)) {
        e[l.column] = 'Enter a whole number, or leave it blank for no limit';
      }
    }
    if (!isWholeNumber(form.sort_order)) e.sort_order = 'Enter a whole number, 0 or more';
    for (const c of PRICE_CURRENCIES) {
      const { monthly, yearly } = form.prices[c];
      if (monthly.trim() === '' && yearly.trim() === '') continue;
      const amountPattern = new RegExp(`^\\d+(\\.\\d{1,${getCurrencyDecimals(c)}})?$`);
      if (!amountPattern.test(monthly.trim())) e[`price_${c}_monthly`] = 'Enter both prices, or neither';
      if (!amountPattern.test(yearly.trim())) e[`price_${c}_yearly`] = 'Enter both prices, or neither';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const limits = {} as Record<MeteredLimitColumn, number | null>;
      for (const l of METERED_LIMITS) limits[l.column] = blankToNull(form.limits[l.column]);
      const prices: PlanCurrencyPrice[] = PRICE_CURRENCIES.filter(
        (c) => form.prices[c].monthly.trim() !== '',
      ).map((c) => ({
        currency: c,
        price_monthly: majorToAmount(Number(form.prices[c].monthly), c),
        price_yearly: majorToAmount(Number(form.prices[c].yearly), c),
      }));
      const fields = {
        display_name: form.display_name.trim(),
        description: form.description.trim() || undefined,
        price_monthly: Math.round(Number(form.price_monthly_rupees) * subunit),
        price_yearly: Math.round(Number(form.price_yearly_rupees) * subunit),
        max_locations: Number(form.max_locations),
        max_users: Number(form.max_users),
        max_requests_per_minute: blankToNull(form.max_requests_per_minute),
        ...limits,
        sort_order: Number(form.sort_order),
        // Registry order, whatever order the boxes were ticked in.
        features: PLAN_FEATURE_KEYS.filter((k) => form.features.includes(k)),
        show_on_website: form.show_on_website,
        badge: form.badge.trim() || null,
        support_level: form.support_level || null,
        prices,
      };

      if (isEdit && plan) {
        const payload: UpdatePlanPayload = { ...fields, is_active: form.is_active };
        await subscriptionService.updatePlan(plan.id, payload);
        toast({ title: `Plan "${form.display_name}" updated`, variant: 'success' });
      } else {
        const payload: CreatePlanPayload = { name: form.name.trim(), ...fields };
        await subscriptionService.createPlan(payload);
        toast({ title: `Plan "${form.display_name}" created`, variant: 'success' });
      }
      onSaved();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save plan';
      toast({ title: message, variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const allSelected = form.features.length === PLAN_FEATURE_KEYS.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit plan — ${plan?.display_name}` : 'Create a new plan'}
      variant="wide"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={submitting}>
            {isEdit ? 'Save changes' : 'Create plan'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="plan-name" className={LABEL_CLASS}>
              Slug
            </label>
            <Input
              id="plan-name"
              value={form.name}
              onChange={(v) => setField('name', v.toUpperCase())}
              placeholder="PRO_PLUS"
              disabled={isEdit}
              error={errors.name}
            />
            <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
              Immutable after creation. UPPER_SNAKE_CASE.
            </p>
          </div>
          <div>
            <label htmlFor="plan-display-name" className={LABEL_CLASS}>
              Display name
            </label>
            <Input
              id="plan-display-name"
              value={form.display_name}
              onChange={(v) => setField('display_name', v)}
              placeholder="Pro Plus"
              error={errors.display_name}
            />
          </div>
        </div>

        <div>
          <label htmlFor="plan-description" className={LABEL_CLASS}>
            Description
          </label>
          <Input
            id="plan-description"
            value={form.description}
            onChange={(v) => setField('description', v)}
            placeholder="One-line pitch shown on the plan card"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="plan-price-monthly" className={LABEL_CLASS}>
              {currencySymbol} / month
            </label>
            <Input
              id="plan-price-monthly"
              type="number"
              value={form.price_monthly_rupees}
              onChange={(v) => setField('price_monthly_rupees', v)}
              error={errors.price_monthly_rupees}
            />
          </div>
          <div>
            <label htmlFor="plan-price-yearly" className={LABEL_CLASS}>
              {currencySymbol} / year
            </label>
            <Input
              id="plan-price-yearly"
              type="number"
              value={form.price_yearly_rupees}
              onChange={(v) => setField('price_yearly_rupees', v)}
              error={errors.price_yearly_rupees}
            />
          </div>
          <div>
            <label htmlFor="plan-sort-order" className={LABEL_CLASS}>
              Sort order
            </label>
            <Input
              id="plan-sort-order"
              type="number"
              min={0}
              value={form.sort_order}
              onChange={(v) => setField('sort_order', v)}
              error={errors.sort_order}
            />
            <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
              Lower numbers show first.
            </p>
          </div>
        </div>

        {/* Prices in other currencies */}
        <div className="rounded-xl bg-indigo-50/60 dark:bg-indigo-900/20 p-4 transition-all duration-200">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500" />
            <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
              Prices in other currencies
            </p>
            <p className="text-xs text-indigo-700/80 dark:text-indigo-300/80">
              Leave both blank and businesses in that currency see and pay the INR price.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {PRICE_CURRENCIES.map((c) => (
              <div key={c} className="grid grid-cols-[3.5rem_1fr_1fr] items-start gap-2">
                <span className="pt-2.5 text-sm font-semibold tabular-nums text-neutral-700 dark:text-neutral-200">
                  {c}
                </span>
                <Input
                  id={`plan-price-${c}-monthly`}
                  type="number"
                  aria-label={`${c} / month`}
                  placeholder="/ month"
                  value={form.prices[c].monthly}
                  onChange={(v) => setPrice(c, 'monthly', v)}
                  error={errors[`price_${c}_monthly`]}
                />
                <Input
                  id={`plan-price-${c}-yearly`}
                  type="number"
                  aria-label={`${c} / year`}
                  placeholder="/ year"
                  value={form.prices[c].yearly}
                  onChange={(v) => setPrice(c, 'yearly', v)}
                  error={errors[`price_${c}_yearly`]}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Limits */}
        <div className="rounded-xl bg-teal-50/60 dark:bg-teal-900/20 p-4 transition-all duration-200">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500" />
            <p className="text-sm font-semibold text-teal-900 dark:text-teal-100">Limits</p>
            <p className="text-xs text-teal-700/80 dark:text-teal-300/80">
              Leave a limit blank for no limit.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="plan-max-locations" className={LABEL_CLASS}>
                Max locations
              </label>
              <Input
                id="plan-max-locations"
                type="number"
                min={1}
                value={form.max_locations}
                onChange={(v) => setField('max_locations', v)}
                error={errors.max_locations}
              />
            </div>
            <div>
              <label htmlFor="plan-max-users" className={LABEL_CLASS}>
                Max users
              </label>
              <Input
                id="plan-max-users"
                type="number"
                min={1}
                value={form.max_users}
                onChange={(v) => setField('max_users', v)}
                error={errors.max_users}
              />
            </div>
            {METERED_LIMITS.map((l) => (
              <div key={l.column}>
                <label htmlFor={`plan-${l.column}`} className={LABEL_CLASS}>
                  {limitInputLabel(l)}
                </label>
                <Input
                  id={`plan-${l.column}`}
                  type="number"
                  min={0}
                  value={form.limits[l.column]}
                  onChange={(v) => setLimit(l.column, v)}
                  error={errors[l.column]}
                  placeholder="No limit"
                />
              </div>
            ))}
            <div className="md:col-span-2">
              <label htmlFor="plan-max-requests-per-minute" className={LABEL_CLASS}>
                API requests per minute
              </label>
              <Input
                id="plan-max-requests-per-minute"
                type="number"
                min={1}
                value={form.max_requests_per_minute}
                onChange={(v) => setField('max_requests_per_minute', v)}
                error={errors.max_requests_per_minute}
                placeholder="No limit"
              />
              <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                One budget across every endpoint. A busy till runs 20–40 requests a minute; billing,
                orders and sync are exempt.
              </p>
            </div>
          </div>
          <p className="mt-3 text-xs text-teal-700/80 dark:text-teal-300/80">
            Bills are never blocked. The owner is warned at 80% and 100%.
          </p>
        </div>

        {/* Pricing page */}
        <div className="rounded-xl bg-violet-50/60 dark:bg-violet-900/20 p-4 transition-all duration-200">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-gradient-to-br from-violet-500 to-pink-500" />
            <p className="text-sm font-semibold text-violet-900 dark:text-violet-100">Website</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Toggle
              id="plan-show-on-website"
              label="Show on website"
              description="List this plan on the orio360.com pricing page."
              checked={form.show_on_website}
              onChange={(v) => setField('show_on_website', v)}
            />
            <div>
              <label htmlFor="plan-badge" className={LABEL_CLASS}>
                Badge
              </label>
              <Input
                id="plan-badge"
                value={form.badge}
                onChange={(v) => setField('badge', v.slice(0, 30))}
                placeholder="Most popular"
              />
              <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                A short label on the plan card. Up to 30 letters.
              </p>
            </div>
            <Select
              id="plan-support-level"
              label="Support"
              options={SUPPORT_LEVEL_OPTIONS}
              value={form.support_level}
              onChange={(v) =>
                setField(
                  'support_level',
                  (Array.isArray(v) ? v[0] : v) as FormState['support_level'],
                )
              }
            />
          </div>
        </div>

        {/* Features */}
        <div className="rounded-xl bg-indigo-50/50 dark:bg-indigo-900/20 p-4 transition-all duration-200">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500" />
              <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">Features</p>
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium tabular-nums text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200">
                {form.features.length} of {PLAN_FEATURE_KEYS.length}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setField('features', allSelected ? [] : [...PLAN_FEATURE_KEYS])}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
          </div>
          <p className="mb-3 text-xs text-indigo-700/80 dark:text-indigo-300/80">
            Billing, products, customers and the main reports are in every plan.
          </p>
          <div className="space-y-4">
            {FEATURE_GROUPS.map((group) => (
              <div key={group.title}>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
                  {group.title}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {group.features.map((f) => (
                    <div
                      key={f.key}
                      className={cn(
                        'rounded-xl border px-3 py-2 transition-all duration-200',
                        form.features.includes(f.key)
                          ? 'border-indigo-200 bg-white dark:border-indigo-700 dark:bg-neutral-800'
                          : 'border-transparent bg-white/60 hover:bg-white dark:bg-neutral-800/60 dark:hover:bg-neutral-800',
                      )}
                    >
                      <Checkbox
                        id={`plan-feature-${f.key}`}
                        label={f.label}
                        checked={form.features.includes(f.key)}
                        onChange={(on) => setFeature(f.key, on)}
                      />
                      <p className="ml-7 text-xs text-neutral-500 dark:text-neutral-400">
                        {f.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {isEdit && (
          <div className="flex items-center justify-between rounded-xl bg-amber-50/60 dark:bg-amber-900/20 p-3 text-sm">
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">Plan visibility</p>
              <p className="text-xs text-amber-700/80 dark:text-amber-300/80">
                Inactive plans are hidden from tenants but remain in use by existing subscribers.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.is_active}
                onChange={(v) => setField('is_active', v)}
              />
              <span className="text-amber-900 dark:text-amber-100 font-medium">Active</span>
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
}
